import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";
import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactCode } from "./ArtifactCode";
import { ArtifactLineage } from "./ArtifactLineage";
import { ArtifactToolbar } from "./ArtifactToolbar";

interface ArtifactShellProps {
  preview: ArtifactPreview;
  // The body — type-specific content (table, image, JSON, etc.). Per-type
  // renderers are responsible for nothing but this slot.
  children: ReactNode;
  // Optional larger / richer view rendered inside a modal when the user
  // clicks the toolbar's expand action. When omitted, expand is hidden.
  // Renderers use this for "full data" (dataframe) or "true size"
  // (figure, image) views.
  expandedContent?: ReactNode;
  className?: string;
}

// Artifact types that have meaningful upstream provenance. Value/text/blob
// are either terminal or have no notion of provenance in this system.
const LINEAGE_TYPES: ReadonlySet<ArtifactPreview["artifact_type"]> = new Set([
  "dataframe",
  "figure",
]);

const TYPE_LABELS: Record<string, string> = {
  dataframe: "Table",
  figure: "Chart",
  image: "Image",
  value: "Value",
  text: "File",
  blob: "File",
};

type View = "data" | "code" | "lineage";

interface ArtifactTabsProps {
  view: View;
  onViewChange: (v: View) => void;
  typeLabel: string;
  hasCode: boolean;
  hasLineage: boolean;
}

const ArtifactTabs = ({
  view,
  onViewChange,
  typeLabel,
  hasCode,
  hasLineage,
}: ArtifactTabsProps) => (
  <Tabs value={view} onValueChange={(v) => onViewChange(v as View)}>
    <TabsList>
      <TabsTrigger value="data">{typeLabel}</TabsTrigger>
      {hasCode && <TabsTrigger value="code">Code</TabsTrigger>}
      {hasLineage && <TabsTrigger value="lineage">Lineage</TabsTrigger>}
    </TabsList>
  </Tabs>
);

interface ArtifactViewContentProps {
  view: View;
  code: string | null;
  artifactId: string;
  children: ReactNode; // rendered when view === "data"
}

const ArtifactViewContent = ({ view, code, artifactId, children }: ArtifactViewContentProps) => {
  if (view === "code" && code) {
    return <ArtifactCode code={code} />;
  }
  if (view === "lineage") {
    return <ArtifactLineage artifactId={artifactId} />;
  }
  return <>{children}</>;
};

// Null when the shell has no expandedContent and therefore no modal to open.
const ArtifactExpandContext = createContext<(() => void) | null>(null);

export const useArtifactExpand = (): (() => void) | null => useContext(ArtifactExpandContext);

// Uniform card chrome around every artifact. Layout:
//
//   ┌──────────────────────────────────────────────────────┐
//   │ [Dataframe | Code | Lineage]          [↓] [↑] [⤢]    │  ← header
//   ├──────────────────────────────────────────────────────┤
//   │ <body>  -or-  <code panel>  -or-  <lineage panel>    │
//   │ summary (italic, no divider — visually grouped       │
//   │   with the body it describes)                        │
//   └──────────────────────────────────────────────────────┘
//
// The tab group on the left labels the artifact and lets the user swap
// between the artifact body, its source code, and its lineage. Tabs that
// don't apply (no code, non-derived type) are hidden. The right-side
// toolbar carries Download/Export/Expand actions only.
export const ArtifactShell = ({
  preview,
  children,
  expandedContent,
  className,
}: ArtifactShellProps) => {
  const [view, setView] = useState<View>("data");
  const [expandOpen, setExpandOpen] = useState(false);
  const requestExpand = useCallback(() => setExpandOpen(true), []);
  const title = preview.artifact_name ?? preview.filename;
  const hasSummary = Boolean(preview.summary) && preview.artifact_type !== "image";
  const hasCode = Boolean(preview.code) && preview.artifact_type !== "image";
  const hasLineage = LINEAGE_TYPES.has(preview.artifact_type);
  const typeLabel = TYPE_LABELS[preview.artifact_type] ?? preview.artifact_type;

  return (
    <div
      className={cn(
        "my-3 overflow-hidden rounded-lg border border-border bg-background/60",
        className
      )}
    >
      <div className="flex items-center border-b border-border px-3 py-2">
        {(hasCode || hasLineage) && (
          <ArtifactTabs
            view={view}
            onViewChange={setView}
            typeLabel={typeLabel}
            hasCode={hasCode}
            hasLineage={hasLineage}
          />
        )}
        <div className="ml-auto">
          <ArtifactToolbar
            preview={preview}
            onExpand={expandedContent ? requestExpand : undefined}
          />
        </div>
      </div>
      <div className="px-3 py-4">
        <ArtifactViewContent view={view} code={preview.code} artifactId={preview.artifact_id}>
          <ArtifactExpandContext.Provider value={expandedContent ? requestExpand : null}>
            {children}
          </ArtifactExpandContext.Provider>
        </ArtifactViewContent>
      </div>
      {hasSummary && (
        <div className="px-3 pb-2 text-[11px] italic leading-snug text-muted-foreground">
          {preview.summary}
        </div>
      )}
      {expandedContent && (
        <Dialog open={expandOpen} onOpenChange={setExpandOpen}>
          <DialogContent
            className="flex h-[calc(100vh-48px)] w-[calc(100vw-48px)] max-w-none flex-col gap-0 overflow-hidden p-4 pt-2 sm:max-w-none"
            overlayClassName="bg-black/70"
          >
            <DialogTitle className="sr-only">{title ?? preview.artifact_type}</DialogTitle>
            <DialogDescription className="sr-only">
              {preview.summary ?? `Expanded view of ${title ?? preview.artifact_type}`}
            </DialogDescription>
            <div className="flex shrink-0 items-center border-b border-border py-2">
              <ArtifactTabs
                view={view}
                onViewChange={setView}
                typeLabel={typeLabel}
                hasCode={hasCode}
                hasLineage={hasLineage}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-auto pt-4">
              <ArtifactViewContent view={view} code={preview.code} artifactId={preview.artifact_id}>
                <>
                  {expandedContent}
                  {hasSummary && (
                    <div className="mt-3 px-1 text-[11px] italic leading-snug text-muted-foreground">
                      {preview.summary}
                    </div>
                  )}
                </>
              </ArtifactViewContent>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
